import React from 'react';
import { ImageIcon } from 'lucide-react';
import { Container, Eyebrow, Section } from '../components/ui/Section';
import { PhotoFrame } from '../components/ui/PhotoFrame';
import { ButtonExternal, ButtonLink } from '../components/ui/Button';
import { LeaderCard } from '../components/about/LeaderCard';
import { Reveal, stagger } from '../components/motion/Reveal';
import { FloatingPhoto } from '../components/motion/FloatingPhoto';
import { DoodleStar, DoodleUnderline } from '../components/brand/Doodles';
import { leadership } from '../data/leadership';
import { galleryPhotos } from '../data/photos';
import { externalLinks, scheduleDisclaimer } from '../data/site';
const schools = ['University of Central Florida', 'Valencia College', 'Seminole State', 'Full Sail University'];
const areas = [{
  title: 'Worship',
  copy: 'Sing, pray, and hear a Bible-centered message together.'
}, {
  title: 'Bible studies',
  copy: 'Study Scripture in smaller groups and ask honest questions.'
}, {
  title: 'Community and fellowship',
  copy: 'Share meals, hang out, and build friendships that last past one semester.'
}, {
  title: 'Evangelism and campus outreach',
  copy: 'Meet students across campus and share the hope of the Gospel.'
}, {
  title: 'Service',
  copy: 'Serve the campus and the surrounding community together.'
}, {
  title: 'Missions',
  copy: 'Take part in mission opportunities offered during the year.'
}, {
  title: 'Intramural sports and recreation',
  copy: 'Play intramurals and join recreational nights like pickleball.'
}];
const pillars = [{
  title: 'Bring In',
  copy: 'Welcome students and help them encounter the Gospel in a genuine Christian community.'
}, {
  title: 'Build Up',
  copy: 'Help students grow in faith, friendship, service, and leadership.'
}, {
  title: 'Send Out',
  copy: 'Equip students to live out their faith and serve on campus and beyond.'
}];
export function About() {
  return <>
      {/* Hero */}
      <Section className="bcm-paper overflow-hidden py-14 pb-24 sm:py-20 sm:pb-28" labelledBy="about-heading" data-id="element-469" data-mp="1ovo2q4r20j6h">
        <Container className="grid items-start gap-10 lg:grid-cols-[1.05fr_0.8fr] lg:gap-14" data-id="element-470" data-mp="1swrikr93qf7p">
          <Reveal data-id="element-471" data-mp="1xujbnolhg7dh">
            <Eyebrow data-id="element-472" data-mp="1xu032yhq2vc4">About UCF BCM</Eyebrow>
            <h1 id="about-heading" className="mt-4 max-w-2xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-brand-navy sm:text-6xl" data-id="element-473" data-mp="22mkujtd5owch">
              A place to belong, ask questions, and grow
            </h1>
            <DoodleUnderline className="mt-3 h-3.5 w-44" color="var(--bcm-yellow)" data-id="element-474" data-mp="2cm1yo6crf26c" />
            <p className="mt-6 max-w-xl font-sans text-[17px] leading-relaxed text-ink" data-id="element-475" data-mp="2gjpv5du3p30c">
              BCM is a place for students to explore the claims of Jesus Christ and experience the
              excitement of answering the call to follow Him.
            </p>
            <p className="mt-4 max-w-xl font-sans text-[17px] leading-relaxed text-ink" data-id="element-476" data-mp="r7xkzd4e4vwk">
              BCM stands for Baptist Collegiate Ministries, but you do not need to be Baptist—or even
              Christian—to find a home here. Students come from many denominations, religious
              backgrounds, schools, and life experiences. Everyone is welcome.
            </p>
          </Reveal>
          {/* A larger photo settles while a small card rises beside it. */}
          <Reveal x={16} delay={0.12} className="relative" data-id="element-477" data-mp="12mr1s206506w">
            <DoodleStar className="absolute -left-4 -top-5 z-10 h-10 w-10" color="var(--bcm-pink)" data-id="element-478" data-mp="12k4u82da2koo" />
            <FloatingPhoto range={6} duration={7.5} data-id="element-479" data-mp="xl1tuv88t2n1">
              <PhotoFrame photo={galleryPhotos[1]} ratio="aspect-[4/5]" rotate="-rotate-1" className="transition-transform duration-300 hover:rotate-0" data-id="element-480" data-mp="sn09m6z2yx9z" />
            </FloatingPhoto>
            <FloatingPhoto range={4} duration={6} delay={0.9} className="absolute -bottom-6 right-0 w-1/3 sm:w-2/5" data-id="element-481" data-mp="39coxjzvi0feq">
              <PhotoFrame photo={galleryPhotos[2]} ratio="aspect-square" rotate="rotate-3" data-id="element-482" data-mp="1125zxq3sba5x" />
            </FloatingPhoto>
          </Reveal>
        </Container>
      </Section>

      {/* Who BCM serves */}
      <Section className="bg-[#fffdf6] py-14 sm:py-18" labelledBy="about-serves-heading" data-id="element-483" data-mp="r3ylpf0vr67m">
        <Container className="grid gap-8 lg:grid-cols-[1fr_0.9fr] lg:gap-14" data-id="element-484" data-mp="w2tffc0dyf6i">
          <div data-id="element-485" data-mp="1jv03zozvdhvd">
            <h2 id="about-serves-heading" className="font-serif text-3xl font-semibold leading-tight text-brand-navy sm:text-4xl" data-id="element-486" data-mp="1jsdwfpczb2d5">
              Serving students across Central Florida
            </h2>
            <p className="mt-5 max-w-xl font-sans text-[17px] leading-relaxed text-ink" data-id="element-487" data-mp="1euuh0m4jojjm">
              UCF BCM primarily serves students at the University of Central Florida and also
              welcomes students from surrounding colleges, including Valencia College, Seminole
              State, and Full Sail University.
            </p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2 lg:content-start" data-id="element-488" data-mp="3s46lkxkbt1da">
            {schools.map(school => <li key={school} className="rounded-2xl border-2 border-brand-navy bg-brand-tan px-5 py-4 font-display text-base font-semibold text-brand-navy" data-id="element-489" data-mp="5uvmb054uh79">
                {school}
              </li>)}
          </ul>
        </Container>
      </Section>

      {/* What BCM does */}
      <Section className="bg-brand-blue py-14 sm:py-18" labelledBy="about-does-heading" data-id="element-490" data-mp="34dkf3apyzp5e">
        <Container data-id="element-491" data-mp="3j1nl2k40j0u7">
          <h2 id="about-does-heading" className="font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-navy sm:text-5xl" data-id="element-492" data-mp="3j273e1xkrr8r">
            Faith lived out together
          </h2>
          <p className="mt-4 max-w-2xl font-sans text-[16px] leading-relaxed text-ink" data-id="element-493" data-mp="2qj085rz66s47">
            These are the ways BCM students gather and serve throughout the year. Not every activity
            happens every week — check the Events page for what is currently scheduled.
          </p>
          <p className="mt-4 font-meta text-[12.5px] leading-relaxed text-ink" data-id="element-494" data-mp="3f9nymz95swof">
            {scheduleDisclaimer}
          </p>
          <ul className="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-495" data-mp="3p1k6v34ym36d">
            {areas.map((area, index) => <Reveal key={area.title} as="li" delay={stagger(index % 3)} className="rounded-[20px] border-2 border-brand-navy bg-white p-6 transition-transform duration-200 hover:-translate-y-1" data-id="element-496" data-mp="2sbt1u9z1kxmb" data-mpi={index}>
                <h3 className="font-display text-lg font-bold leading-snug text-brand-navy" data-id="element-497" data-mp="36b2l5iijo7ui">
                  {area.title}
                </h3>
                <p className="mt-2 font-sans text-[15px] leading-relaxed text-ink" data-id="element-498" data-mp="gkyvxgsrp00m">{area.copy}</p>
              </Reveal>)}
          </ul>
        </Container>
      </Section>

      {/* Mission */}
      <Section className="on-dark bg-brand-navy py-14 text-white sm:py-20" labelledBy="about-mission-heading" data-id="element-499" data-mp="261kr9qs15cnp">
        <Container data-id="element-500" data-mp="214kuk6h2lm99">
          <p className="font-meta text-xs font-semibold uppercase tracking-[0.2em] text-brand-blue" data-id="element-501" data-mp="2165dszxxpirt">
            Our Mission
          </p>
          <h2 id="about-mission-heading" className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-id="element-502" data-mp="1rerk58snzd6l">
            Bring in. Build up. Send out.
          </h2>
          <ul className="mt-10 grid gap-8 sm:grid-cols-3" data-id="element-503" data-mp="1ic9z2xoqwql6">
            {pillars.map((pillar, index) => <Reveal key={pillar.title} as="li" delay={stagger(index)} className="border-t-4 border-brand-yellow pt-5" data-id="element-504" data-mp="1dezz4o3qcwj3" data-mpi={index}>
                <h3 className="font-display text-2xl font-bold text-brand-blue" data-id="element-505" data-mp="37qy58h6qtcar">{pillar.title}</h3>
                <p className="mt-2 font-sans text-[16px] leading-relaxed text-white" data-id="element-506" data-mp="32ug37p2srs24">
                  {pillar.copy}
                </p>
              </Reveal>)}
          </ul>
        </Container>
      </Section>

      {/* Location */}
      <Section className="bcm-paper py-14 sm:py-18" labelledBy="about-location-heading" data-id="element-507" data-mp="22vgytni2d7s6">
        <Container data-id="element-508" data-mp="22to0qwlinvvs">
          <h2 id="about-location-heading" className="font-serif text-3xl font-semibold leading-tight text-brand-navy sm:text-4xl" data-id="element-509" data-mp="26wu3vvdc16y1">
            Where BCM meets
          </h2>
          <p className="mt-5 max-w-2xl font-sans text-[17px] leading-relaxed text-ink" data-id="element-510" data-mp="1xwxetsn1662g">
            During the school year, BCM meets Thursdays at 7:30 PM. Most weeks, worship is held on
            the third floor of the UCF Student Union in Cape Florida Ballroom, Room 316. Check the
            Events page or Instagram for the latest location.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-511" data-mp="3e8t8if0i8km9">
            <ButtonLink to="/events" data-id="element-512" data-mp="3ibg3gosh2lno">View Events</ButtonLink>
            <ButtonExternal href={externalLinks.studentUnion} variant="secondary" data-id="element-513" data-mp="16o49hpjjctms">
              UCF Student Union Directions
            </ButtonExternal>
            <ButtonExternal href={externalLinks.capeFloridaBallroom} variant="secondary" data-id="element-514" data-mp="12lhbf6w9uch5">
              Cape Florida Ballroom Information
            </ButtonExternal>
          </div>
        </Container>
      </Section>

      {/* Leadership */}
      <Section className="bg-[#fffdf6] py-14 sm:py-18" labelledBy="about-leaders-heading" data-id="element-515" data-mp="1qc3l12lxc4t9">
        <Container data-id="element-516" data-mp="1lebhfbbx2u98">
          <h2 id="about-leaders-heading" className="font-serif text-3xl font-semibold leading-tight text-brand-navy sm:text-4xl" data-id="element-517" data-mp="1lcqotr95xle0">
            Meet the BCM leadership team
          </h2>
          <p className="mt-4 max-w-2xl font-sans text-[16px] leading-relaxed text-ink" data-id="element-518" data-mp="1uf9mzy2079tx">
            These are the students and staff serving BCM this year. Reach out any time — someone is
            always glad to answer a question.
          </p>
          {/*
            INTERNAL CONTENT NOTE (not rendered): leader photographs are expected
            before the fall launch. Add each hosted URL to data/leadership.ts.
           */}
          <ul className="mt-9 grid grid-cols-2 gap-4 md:grid-cols-5" data-id="element-519" data-mp="33db4ku6xznvn">
            {leadership.map((leader, index) => <Reveal key={leader.id} as="li" delay={stagger(index % 5)} className="h-full" data-id="element-520" data-mp="2yezxgr817rh4" data-mpi={index}>
                <LeaderCard leader={leader} index={index} data-id="element-521" data-mp="3v50icf5a5nt5" />
              </Reveal>)}
          </ul>
          <div className="mt-8" data-id="element-522" data-mp="3r2nejwa3fzz0">
            <ButtonExternal href={externalLinks.getConnectedForm} variant="secondary" data-id="element-523" data-mp="3r3p1oz4dwcx3">
              Contact a BCM Leader
            </ButtonExternal>
          </div>
        </Container>
      </Section>

      {/* Worship team */}
      <Section className="bg-brand-blue py-14 sm:py-18" labelledBy="about-worship-heading" data-id="element-524" data-mp="in3of64pyxo6">
        <Container className="grid items-center gap-10 lg:grid-cols-[0.9fr_1fr] lg:gap-14" data-id="element-525" data-mp="iowzcxmd841z">
          <div data-id="element-526" data-mp="duanktcdg9hd">
            <h2 id="about-worship-heading" className="font-serif text-3xl font-semibold leading-tight text-brand-navy sm:text-4xl" data-id="element-527" data-mp="l7i8dekr69k5">
              Worship at BCM
            </h2>
            <p className="mt-5 font-sans text-[17px] leading-relaxed text-ink" data-id="element-528" data-mp="v56d8xc2oxg1">
              BCM worship nights give students space to worship together, hear a Bible-centered
              message, and build community.
            </p>
          </div>
          {/*
            Placeholder only. A new worship-team photo is expected; do not reuse an
            outdated worship-team photo unless the content owner selects it.
           */}
          <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-3 rounded-[20px] border-2 border-brand-navy bg-brand-tan p-8 text-center" data-id="element-529" data-mp="1dwpcct2z7rcs">
            <ImageIcon aria-hidden="true" className="h-7 w-7 text-brand-navy" data-id="element-530" data-mp="1mxnbawyk58oi" />
            <p className="font-meta text-[15px] font-semibold text-brand-navy" data-id="element-531" data-mp="1rvp100qho68y">Add Image here</p>
          </div>
        </Container>
      </Section>
    </>;
}